import { useState, useEffect, useCallback } from "react";
import {
  Sparkles, Sliders, RotateCcw, Copy, Send, ChevronDown,
  AlertTriangle, CheckCircle2, Zap, FileText, Globe, ArrowRight,
  BookOpen, Newspaper, ShoppingBag, Shield, Briefcase, Plane,
  Mic, Target, Lightbulb, Loader2, Save, ChevronLeft, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { safeMarkdownHTML } from "@/lib/safeHtml";
import { toast } from "sonner";

const presets = [
  { id: "breaking", label: "Breaking News", icon: Zap, desc: "Fast, factual, urgent tone" },
  { id: "evergreen", label: "Evergreen Article", icon: BookOpen, desc: "Timeless, in-depth, authoritative" },
  { id: "seo-pillar", label: "SEO Pillar Page", icon: Target, desc: "Keyword-rich, structured, comprehensive" },
  { id: "magazine", label: "Magazine Style", icon: Newspaper, desc: "Editorial, narrative, engaging" },
  { id: "affiliate", label: "Affiliate Review", icon: ShoppingBag, desc: "Product-focused, persuasive, CTA-driven" },
  { id: "neutral", label: "Neutral Newsroom", icon: Briefcase, desc: "Balanced, objective, AP style" },
  { id: "local-news", label: "Local News", icon: Globe, desc: "Community-focused, relatable" },
  { id: "cybersecurity", label: "Cybersecurity Brief", icon: Shield, desc: "Technical, precise, actionable" },
  { id: "travel", label: "Travel Feature", icon: Plane, desc: "Vivid, sensory, inspirational" },
  { id: "opinion", label: "Opinion / Editorial", icon: Mic, desc: "Persuasive, voice-driven, bold" },
];

const tones = ["Neutral", "Formal", "Casual", "Authoritative", "Conversational", "Academic", "Journalistic"];
const lengths = ["Short (300w)", "Medium (600w)", "Long (1200w)", "Extended (2000w)"];

interface Article {
  id: string;
  title: string;
  content: string | null;
  status: string;
  slug: string;
  company_id: string;
}

type DbError = { message: string };
type DbResult<T = unknown> = { data: T | null; error: DbError | null };
type LooseQuery<T = unknown> = PromiseLike<DbResult<T>> & {
  select: (columns?: string) => LooseQuery<T>;
  order: (column: string, options?: { ascending?: boolean }) => LooseQuery<T>;
  limit: (count: number) => LooseQuery<T>;
  eq: (column: string, value: unknown) => LooseQuery<T>;
  update: (values: Record<string, unknown>) => LooseQuery<T>;
};
type LooseSupabaseClient = { from: (table: string) => LooseQuery };

const aiStudioDb = supabase as unknown as LooseSupabaseClient;

export default function AIStudioPage() {
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const [selectedPreset, setSelectedPreset] = useState("seo-pillar");
  const [intensity, setIntensity] = useState(75);
  const [selectedTone, setSelectedTone] = useState("Journalistic");
  const [selectedLength, setSelectedLength] = useState("Long (1200w)");
  const [showPresets, setShowPresets] = useState(false);
  const [keyword, setKeyword] = useState("");

  // Articles state
  const [articles, setArticles] = useState<Article[]>([]);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [originalText, setOriginalText] = useState("");
  const [rewrittenText, setRewrittenText] = useState("");
  const [isRewriting, setIsRewriting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(true);

  // Fetch articles
  useEffect(() => {
    async function load() {
      setLoadingArticles(true);
      let query = aiStudioDb
        .from("articles")
        .select("id, title, content, status, slug, company_id")
        .order("created_at", { ascending: false })
        .limit(50);
      if (currentCompanyId) query = query.eq("company_id", currentCompanyId);
      const { data } = await query;
      setArticles((data || []) as Article[]);
      setLoadingArticles(false);
    }
    load();
  }, [currentCompanyId]);

  // When article selected, populate original text
  useEffect(() => {
    if (selectedArticleId) {
      const a = articles.find((x) => x.id === selectedArticleId);
      setOriginalText(a?.content || "");
      setRewrittenText("");
    }
  }, [selectedArticleId, articles]);

  const originalWords = originalText.trim() ? originalText.split(/\s+/).length : 0;
  const rewrittenWords = rewrittenText.trim() ? rewrittenText.split(/\s+/).length : 0;

  const handleRewrite = useCallback(async () => {
    if (!originalText.trim() || originalText.trim().length < 10) {
      toast.error("Le texte source doit contenir au moins 10 caractères.");
      return;
    }

    if (!currentCompanyId) {
      toast.error("Select a company before using AI rewrite.");
      return;
    }

    setIsRewriting(true);
    setRewrittenText("");

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        toast.error("Session expired. Please sign in again.");
        setIsRewriting(false);
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/rewrite-article`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            content: originalText,
            article_id: selectedArticleId,
            company_id: currentCompanyId,
            preset: selectedPreset,
            tone: selectedTone,
            intensity,
            targetLength: selectedLength,
            keyword: keyword || undefined,
          }),
        }
      );

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Erreur inconnue" }));
        toast.error(err.error || `Erreur ${resp.status}`);
        setIsRewriting(false);
        return;
      }

      if (!resp.body) {
        toast.error("Pas de réponse du serveur.");
        setIsRewriting(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let nlIdx: number;
        while ((nlIdx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, nlIdx);
          buffer = buffer.slice(nlIdx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (!line.startsWith("data: ")) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;
          try {
            const parsed = JSON.parse(jsonStr);
            const delta = parsed.choices?.[0]?.delta?.content;
            if (delta) {
              fullText += delta;
              setRewrittenText(fullText);
            }
          } catch {
            // partial JSON, ignore
          }
        }
      }

      toast.success("Réécriture terminée !");
    } catch (e) {
      toast.error("Erreur de connexion au service AI.");
    } finally {
      setIsRewriting(false);
    }
  }, [currentCompanyId, originalText, selectedArticleId, selectedPreset, selectedTone, intensity, selectedLength, keyword]);

  const handleSaveAsDraft = useCallback(async () => {
    if (!selectedArticleId || !rewrittenText.trim()) {
      toast.error("Sélectionnez un article et générez une réécriture d'abord.");
      return;
    }

    if (!currentCompanyId) {
      toast.error("Select a company before saving.");
      return;
    }

    setIsSaving(true);
    const { error } = await aiStudioDb
      .from("articles")
      .update({ content: rewrittenText, status: "draft" as const })
      .eq("id", selectedArticleId)
      .eq("company_id", currentCompanyId);

    if (error) {
      toast.error("Erreur lors de la sauvegarde.");
    } else {
      toast.success("Article mis à jour en tant que brouillon.");
      // Update local state
      setArticles((prev) =>
        prev.map((a) => (a.id === selectedArticleId ? { ...a, content: rewrittenText, status: "draft" } : a))
      );
      setOriginalText(rewrittenText);
    }
    setIsSaving(false);
  }, [currentCompanyId, selectedArticleId, rewrittenText]);

  const renderMarkdown = (text: string) => {
    return text.split("\n").map((line, i) => {
      if (line.startsWith("# ")) return <h1 key={i} className="text-xl font-bold text-foreground mt-0 mb-3">{line.slice(2)}</h1>;
      if (line.startsWith("## ")) return <h2 key={i} className="text-base font-semibold text-foreground mt-4 mb-2">{line.slice(3)}</h2>;
      if (line.startsWith("### ")) return <h3 key={i} className="text-sm font-semibold text-foreground mt-3 mb-1">{line.slice(4)}</h3>;
      if (line.startsWith("- **")) return <li key={i} className="text-sm text-foreground/80 ml-4 list-disc mb-1" dangerouslySetInnerHTML={{ __html: safeMarkdownHTML(line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')) }} />;
      if (line.startsWith("- ")) return <li key={i} className="text-sm text-foreground/80 ml-4 list-disc mb-1">{line.slice(2)}</li>;
      if (line.startsWith("**") && line.endsWith("**")) return <p key={i} className="text-sm font-semibold text-foreground my-2">{line.replace(/\*\*/g, "")}</p>;
      if (line.startsWith("*") && line.endsWith("*")) return <p key={i} className="text-sm italic text-muted-foreground my-2">{line.replace(/\*/g, "")}</p>;
      if (line.trim() === "") return <br key={i} />;
      return <p key={i} className="text-sm text-foreground/80 my-1" dangerouslySetInnerHTML={{ __html: safeMarkdownHTML(line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground">$1</strong>')) }} />;
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 backdrop-blur-xl px-6 shrink-0">
        <div>
          <h1 className="text-lg font-semibold text-foreground">AI Studio</h1>
          <p className="text-xs text-muted-foreground">Rewrite, transform & optimize content</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setOriginalText(""); setRewrittenText(""); setSelectedArticleId(null); }}
            className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </button>
          <button
            onClick={handleRewrite}
            disabled={isRewriting || !originalText.trim()}
            className="flex items-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
            title="Propulsé par Google Gemini (US). Voir la politique."
          >
            {isRewriting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {isRewriting ? "Réécriture..." : "Rewrite"}
          </button>
        </div>
        <p className="px-3 pb-2 text-[10px] leading-4 text-muted-foreground">
          Propulsé par Google Gemini (US). Votre contenu est transmis à Google pour la réécriture.{" "}
          <a href="/trust#ai" className="text-primary hover:underline">Voir la politique de confidentialité</a>.
        </p>
      </header>

      <div className="flex flex-1 overflow-hidden animate-slide-in">
        {/* Left Control Panel */}
        <aside className="w-72 border-r border-border bg-card/50 overflow-y-auto shrink-0 p-4 space-y-5">
          {/* Article Selector */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 block">Source Article</label>
            {loadingArticles ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Chargement...
              </div>
            ) : (
              <div className="relative">
                <select
                  value={selectedArticleId || ""}
                  onChange={(e) => setSelectedArticleId(e.target.value || null)}
                  className="w-full appearance-none rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="">— Paste text or select —</option>
                  {articles.map((a) => (
                    <option key={a.id} value={a.id}>{a.title}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              </div>
            )}
          </div>

          {/* Preset Selection */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 block">Prompt Preset</label>
            <button
              onClick={() => setShowPresets(!showPresets)}
              className="w-full flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground hover:border-primary/30 transition-colors"
            >
              <span className="flex items-center gap-2">
                {(() => {
                  const p = presets.find((p) => p.id === selectedPreset);
                  if (!p) return null;
                  return <><p.icon className="h-4 w-4 text-primary" />{p.label}</>;
                })()}
              </span>
              <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", showPresets && "rotate-180")} />
            </button>
            {showPresets && (
              <div className="mt-2 space-y-1 max-h-60 overflow-y-auto">
                {presets.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => { setSelectedPreset(p.id); setShowPresets(false); }}
                    className={cn(
                      "w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                      selectedPreset === p.id ? "bg-primary/10 border border-primary/30" : "hover:bg-accent"
                    )}
                  >
                    <p.icon className={cn("h-4 w-4 shrink-0", selectedPreset === p.id ? "text-primary" : "text-muted-foreground")} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{p.label}</p>
                      <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Rewrite Intensity */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">Rewrite Intensity</label>
              <span className="text-xs font-mono text-primary">{intensity}%</span>
            </div>
            <input type="range" min={10} max={100} value={intensity} onChange={(e) => setIntensity(Number(e.target.value))} className="w-full accent-primary h-1" />
            <div className="flex justify-between mt-1">
              <span className="text-[10px] text-muted-foreground">Light</span>
              <span className="text-[10px] text-muted-foreground">Full</span>
            </div>
          </div>

          {/* Tone */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 block">Tone</label>
            <div className="flex flex-wrap gap-1.5">
              {tones.map((t) => (
                <button
                  key={t}
                  onClick={() => setSelectedTone(t)}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors",
                    selectedTone === t ? "bg-primary/15 text-primary border border-primary/30" : "bg-accent text-muted-foreground hover:text-foreground"
                  )}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Length */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 block">Target Length</label>
            <div className="space-y-1">
              {lengths.map((l) => (
                <button
                  key={l}
                  onClick={() => setSelectedLength(l)}
                  className={cn(
                    "w-full rounded-lg px-3 py-1.5 text-left text-sm transition-colors",
                    selectedLength === l ? "bg-primary/10 text-primary border border-primary/30" : "hover:bg-accent text-muted-foreground"
                  )}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* SEO Keyword */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60 mb-2 block">Target Keyword</label>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="e.g. EU AI Act regulation"
              className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus:ring-1 focus:ring-ring placeholder:text-muted-foreground"
            />
          </div>
        </aside>

        {/* Main Content — Side by Side */}
        <div className="flex-1 flex overflow-hidden">
          {/* Original */}
          <div className="flex-1 border-r border-border flex flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5 bg-card/30 shrink-0">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Original</span>
                <span className="text-xs text-muted-foreground">({originalWords} words)</span>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(originalText); toast.info("Copié !"); }}
                className="rounded p-1 hover:bg-accent transition-colors"
              >
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <textarea
                value={originalText}
                onChange={(e) => { setOriginalText(e.target.value); setSelectedArticleId(null); }}
                placeholder="Collez votre texte source ici ou sélectionnez un article dans le panneau de gauche..."
                className="w-full h-full min-h-[300px] bg-transparent text-sm leading-relaxed text-foreground/80 outline-none resize-none placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          {/* Arrow divider */}
          <div className="flex items-center justify-center w-8 shrink-0">
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-card">
              {isRewriting ? <Loader2 className="h-4 w-4 text-primary animate-spin" /> : <ArrowRight className="h-4 w-4 text-primary" />}
            </div>
          </div>

          {/* Rewritten */}
          <div className="flex-1 flex flex-col">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5 bg-card/30 shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-foreground">Rewritten</span>
                <span className="text-xs text-muted-foreground">({rewrittenWords} words)</span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { navigator.clipboard.writeText(rewrittenText); toast.info("Copié !"); }}
                  className="rounded p-1 hover:bg-accent transition-colors"
                >
                  <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
                <button
                  onClick={handleSaveAsDraft}
                  disabled={!rewrittenText.trim() || !selectedArticleId || isSaving}
                  className="flex items-center gap-1.5 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                >
                  {isSaving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Save as Draft
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {!rewrittenText && !isRewriting && (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground/50 gap-2">
                  <Sparkles className="h-8 w-8" />
                  <p className="text-sm">Le résultat de la réécriture apparaîtra ici</p>
                </div>
              )}
              {(rewrittenText || isRewriting) && (
                <div className="prose prose-sm prose-invert max-w-none text-sm leading-relaxed text-foreground/80">
                  {renderMarkdown(rewrittenText)}
                  {isRewriting && <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-0.5" />}
                </div>
              )}
            </div>

            {/* Bottom analysis bar */}
            {rewrittenText && (
              <div className="border-t border-border bg-card/30 px-4 py-3 shrink-0">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-success" />
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">Words</p>
                      <p className="text-sm font-semibold text-foreground">{rewrittenWords}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-info" />
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">Preset</p>
                      <p className="text-sm font-semibold text-foreground">{presets.find((p) => p.id === selectedPreset)?.label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-warning" />
                    <div>
                      <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/60">Tone</p>
                      <p className="text-sm font-semibold text-foreground">{selectedTone}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
